import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Edit, Trash2, Check, Star, Loader2, Code, Eye } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Alert, AlertDescription } from '../components/ui/alert';
import axios from 'axios';
import BrandLogo from '../components/BrandLogo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const AdminPromptsPage = () => {
  const navigate = useNavigate();
  const [prompts, setPrompts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [previewPrompt, setPreviewPrompt] = useState('');
  const [error, setError] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    prompt_text: '',
    prompt_type: 'fact_check',
    is_default: false
  });

  useEffect(() => {
    checkAdminAccess();
    loadPrompts();
  }, []);

  const checkAdminAccess = () => {
    const user = JSON.parse(localStorage.getItem('user') || 'null');
    if (user && user.is_admin) {
      setIsAdmin(true);
    } else {
      setError('Admin access required');
    }
  };

  const loadPrompts = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/prompts/`);
      setPrompts(response.data);
    } catch (err) {
      setError('Failed to load prompts');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const resetForm = () => {
    setFormData({
      name: '',
      prompt_text: '',
      prompt_type: 'fact_check',
      is_default: false
    });
    setEditingPrompt(null);
  };

  const handleAddPrompt = async () => {
    if (!isAdmin) {
      setError('Admin access required');
      return;
    }

    try {
      const response = await axios.post(`${API}/prompts/`, formData, {
        withCredentials: true
      });
      setPrompts([...prompts, response.data]);
      setShowModal(false);
      resetForm();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to add prompt');
    }
  };

  const handleEditPrompt = (prompt) => {
    setEditingPrompt(prompt);
    setFormData({
      name: prompt.name,
      prompt_text: prompt.prompt_text,
      prompt_type: prompt.prompt_type,
      is_default: prompt.is_default
    });
    setShowModal(true);
  };

  const handleUpdatePrompt = async () => {
    if (!isAdmin || !editingPrompt) return;

    try {
      const response = await axios.put(
        `${API}/prompts/${editingPrompt.prompt_id}`,
        formData,
        { withCredentials: true }
      );
      setPrompts(prompts.map(p => p.prompt_id === editingPrompt.prompt_id ? response.data : p));
      setShowModal(false);
      resetForm();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update prompt');
    }
  };

  const handleDeletePrompt = async (promptId) => {
    if (!isAdmin) return;
    if (!window.confirm('Are you sure you want to deactivate this prompt?')) return;

    try {
      await axios.delete(`${API}/prompts/${promptId}`, {
        withCredentials: true
      });
      setPrompts(prompts.filter(p => p.prompt_id !== promptId));
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete prompt');
    }
  };

  const handleSetDefault = async (promptId) => {
    if (!isAdmin) return;

    try {
      await axios.post(`${API}/prompts/${promptId}/set-default`, {}, {
        withCredentials: true
      });
      loadPrompts(); // Reload to get updated default status
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to set default');
    }
  };

  const handleSeedDefault = async () => {
    if (!isAdmin) return;

    try {
      const response = await axios.post(`${API}/prompts/seed`, {}, {
        withCredentials: true
      });
      alert(response.data.message);
      loadPrompts();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to seed prompt');
    }
  };

  const handlePreview = (promptText) => {
    setPreviewPrompt(promptText);
    setShowPreview(true);
  };

  if (!isAdmin && !isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 flex items-center justify-center">
        <Alert variant="destructive" className="max-w-md">
          <AlertDescription>
            Admin access required. Please contact an administrator.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
      {/* Header */}
      <header className="border-b border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md sticky top-0 z-40">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <BrandLogo size="md" data-testid="brand-logo-header" />
            <span className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-blue-800 dark:from-blue-400 dark:to-blue-600 bg-clip-text text-transparent">
              KaiCheckIt - System Prompts
            </span>
          </div>
          <div className="flex gap-3">
            <Button
              onClick={() => navigate('/admin/sources')}
              variant="outline"
            >
              Manage Sources
            </Button>
            <Button
              onClick={() => navigate('/')}
              variant="outline"
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-6 py-12">
        <div className="max-w-6xl mx-auto">
          {/* Header Section */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-slate-800 dark:text-slate-100 mb-2">System Prompt Management</h1>
              <p className="text-slate-600 dark:text-slate-400">Configure AI system prompts for content verification</p>
            </div>
            <div className="flex gap-3">
              <Button
                onClick={handleSeedDefault}
                variant="outline"
                className="flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Seed Default
              </Button>
              <Button
                onClick={() => {
                  resetForm();
                  setShowModal(true);
                }}
                className="bg-blue-600 hover:bg-blue-700 flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Add Prompt
              </Button>
            </div>
          </div>

          {error && (
            <Alert variant="destructive" className="mb-6">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Info Card */}
          <Card className="mb-6 bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-800">
            <CardContent className="pt-6">
              <p className="text-sm text-slate-700 dark:text-slate-300">
                <strong>Tip:</strong> Use <code className="bg-blue-100 dark:bg-blue-800 px-2 py-0.5 rounded">{"{SOURCE_CONTEXT}"}</code> in your prompt
                where you want trusted sources to be injected. The system will automatically insert your curated source list.
              </p>
            </CardContent>
          </Card>

          {/* Prompts Grid */}
          {isLoading ? (
            <div className="flex justify-center py-20">
              <Loader2 className="w-12 h-12 animate-spin text-blue-600 dark:text-blue-400" />
            </div>
          ) : (
            <div className="space-y-4">
              {prompts.map((prompt) => (
                <Card key={prompt.prompt_id} className={`hover:shadow-lg transition-shadow dark:bg-slate-800 ${prompt.is_default ? 'border-2 border-blue-500' : ''}`}>
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <CardTitle className="text-lg dark:text-slate-100">{prompt.name}</CardTitle>
                          {prompt.is_default && (
                            <Badge className="bg-blue-600 text-white flex items-center gap-1">
                              <Star className="w-3 h-3" />
                              Default
                            </Badge>
                          )}
                          <Badge variant="outline">{prompt.prompt_type}</Badge>
                          <Badge variant="secondary">v{prompt.version}</Badge>
                          {prompt.is_active && (
                            <Badge className="bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300">Active</Badge>
                          )}
                        </div>
                        <CardDescription className="dark:text-slate-400">
                          Created: {new Date(prompt.created_at).toLocaleDateString()} | 
                          Updated: {new Date(prompt.updated_at).toLocaleDateString()}
                        </CardDescription>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handlePreview(prompt.prompt_text)}
                          className="h-8 w-8 p-0"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleEditPrompt(prompt)}
                          className="h-8 w-8 p-0"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        {!prompt.is_default && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleSetDefault(prompt.prompt_id)}
                            className="h-8 w-8 p-0 text-blue-600 dark:text-blue-400"
                          >
                            <Star className="w-4 h-4" />
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeletePrompt(prompt.prompt_id)}
                          className="h-8 w-8 p-0 text-red-600 dark:text-red-400"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="bg-slate-50 dark:bg-slate-900 p-4 rounded-lg border border-slate-200 dark:border-slate-700">
                      <pre className="text-xs text-slate-700 dark:text-slate-300 whitespace-pre-wrap font-mono line-clamp-4">
                        {prompt.prompt_text}
                      </pre>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {prompts.length === 0 && !isLoading && (
            <div className="text-center py-20">
              <Code className="w-16 h-16 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
              <p className="text-slate-600 dark:text-slate-400 mb-4">No prompts added yet</p>
              <Button onClick={handleSeedDefault} className="bg-blue-600 hover:bg-blue-700">
                Seed Default Prompt
              </Button>
            </div>
          )}
        </div>
      </main>

      {/* Add/Edit Modal */}
      <Dialog open={showModal} onOpenChange={(open) => {
        setShowModal(open);
        if (!open) resetForm();
      }}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingPrompt ? 'Edit System Prompt' : 'Add System Prompt'}</DialogTitle>
            <DialogDescription>
              {editingPrompt ? 'Update the system prompt configuration' : 'Create a new system prompt for AI verification'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="name">Prompt Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder="e.g., Default Verification Prompt"
              />
            </div>

            <div>
              <Label htmlFor="prompt_type">Prompt Type</Label>
              <Select value={formData.prompt_type} onValueChange={(value) => handleInputChange('prompt_type', value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="fact_check">Fact-Checking</SelectItem>
                  <SelectItem value="image_analysis">Image Analysis</SelectItem>
                  <SelectItem value="url_extraction">URL Extraction</SelectItem>
                  <SelectItem value="general">General</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="prompt_text">System Prompt</Label>
              <Textarea
                id="prompt_text"
                value={formData.prompt_text}
                onChange={(e) => handleInputChange('prompt_text', e.target.value)}
                placeholder="Enter your system prompt here..."
                className="min-h-[300px] font-mono text-sm"
              />
              <p className="text-xs text-slate-500 mt-1">
                Use {"{SOURCE_CONTEXT}"} where you want trusted sources injected
              </p>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_default"
                checked={formData.is_default}
                onChange={(e) => handleInputChange('is_default', e.target.checked)}
                className="rounded"
              />
              <Label htmlFor="is_default" className="cursor-pointer">
                Set as default prompt for this type
              </Label>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                onClick={editingPrompt ? handleUpdatePrompt : handleAddPrompt}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
              >
                {editingPrompt ? 'Update' : 'Add'} Prompt
              </Button>
              <Button
                onClick={() => {
                  setShowModal(false);
                  resetForm();
                }}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Preview Modal */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Prompt Preview</DialogTitle>
          </DialogHeader>
          <div className="bg-slate-50 p-6 rounded-lg border border-slate-200">
            <pre className="text-sm text-slate-700 whitespace-pre-wrap font-mono">
              {previewPrompt}
            </pre>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPromptsPage;
